import React, { useEffect, useState, useRef } from "react";
import {
	Card,
	Tag,
	Button,
	Upload,
	message,
	Empty,
	Typography,
} from "antd";
import {
	UploadOutlined,
	SendOutlined,
	ReloadOutlined,
	CheckCircleOutlined,
	InfoCircleOutlined,
} from "@ant-design/icons";
import SignatureCanvas from "react-signature-canvas";
import dayjs from "dayjs";
import { useAddIRMutation } from "../../Redux/service/IR/IR";
import { useGetPOIProjectsQuery } from "../../Redux/service/POIS/POIs";
import { toast } from "react-toastify";
import trans from "../../utils";
import { C, S, R, E, T } from "./copilotTheme";

const { Text } = Typography;

/**
 * Convert a data URL to a File object so we can submit it the same way the
 * existing form does (the addIR mutation appends Files via FormData).
 */
function dataUrlToFile(dataUrl, filename) {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)[1];
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) u8arr[n] = bstr.charCodeAt(n);
	return new File([u8arr], filename, { type: mime });
}

/* Shared card chrome so every section matches the chat pane exactly. */
const cardStyles = {
	headStyle: {
		minHeight: 40,
		padding: `0 ${S.lg}px`,
		borderBottom: `1px solid ${C.lineSoft}`,
		fontSize: T.body,
		fontWeight: 600,
		color: C.ink,
	},
	bodyStyle: { padding: S.lg },
	style: {
		marginBottom: S.md,
		borderRadius: R.md,
		border: `1px solid ${C.line}`,
		boxShadow: E.low,
	},
};

function SectionTitle({ icon, children }) {
	return (
		<span style={{ display: "inline-flex", alignItems: "center" }}>
			<span style={{ color: C.accent, marginInlineEnd: S.sm, display: "flex" }}>
				{icon}
			</span>
			{children}
		</span>
	);
}

/**
 * Right-side panel: shows the AI-built IR draft as the user/AI fill it in,
 * lets the user attach files & sign, and submits via the existing
 * useAddIRMutation.
 */
export default function IRDraftPreview({
	draft,
	formSchema,
	onSubmitted,
	onReset,
}) {
	const [addIR, { isLoading: submitting }] = useAddIRMutation();

	// Project name still needs a client lookup (the backend does not resolve
	// a project name into the draft). Zone/Level/Room names, however, are
	// resolved SERVER-SIDE and travel inside the draft as
	// zone_name / level_name / room_name (see resolveLocationNames in
	// tools.dispatcher). We read those directly instead of re-fetching and
	// re-resolving them here — that parallel resolution was the source of
	// the "#1143" bug, since it could silently miss and fall back to #id.
	const { data: projectsResp } = useGetPOIProjectsQuery();

	// File and signature state — these are added in the UI, not by the AI
	// Map of fieldKey -> File[]
	const [filesByField, setFilesByField] = useState({});
	// Map of fieldKey -> dataUrl (signature)
	const [signaturesByField, setSignaturesByField] = useState({});
	const sigCanvasRefs = useRef({});

	// Reset files/signatures when draft is reset
	useEffect(() => {
		if (!draft || Object.keys(draft).length === 0) {
			setFilesByField({});
			setSignaturesByField({});
		}
	}, [draft]);

	const projectName =
		projectsResp?.data?.find((p) => p.id === draft?.project_id)?.name ?? null;
	const zoneName = draft?.zone_name ?? null;
	const levelName = draft?.level_name ?? null;
	const roomName = draft?.room_name ?? null;

	// Determine which UI-handled fields the form has (Uploader / Signature)
	const uiFields =
		formSchema?.fields?.filter((f) => f.handledBy === "ui") ?? [];

	// Are required UI fields satisfied?
	const requiredUiFieldsSatisfied = (formSchema?.fields ?? [])
		.filter((f) => f.handledBy === "ui" && f.required)
		.every((f) => {
			if (f.name === "Uploader") {
				return (filesByField[f.key]?.length ?? 0) > 0;
			}
			if (f.name === "Signature") {
				return Boolean(signaturesByField[f.key]);
			}
			return true;
		});

	// Required AI fields filled?
	const requiredAiFieldsSatisfied = (formSchema?.fields ?? [])
		.filter((f) => f.handledBy === "ai" && f.required)
		.every((f) => {
			const entry = (draft?.fields ?? []).find((x) => x.key === f.key);
			if (!entry) return false;
			const v = entry.value;
			if (v === null || v === undefined || v === "") return false;
			if (Array.isArray(v) && v.length === 0) return false;
			return true;
		});

	const coreFieldsSatisfied = Boolean(
		draft?.type &&
			draft?.project_id &&
			draft?.discipline &&
			draft?.form_id &&
			draft?.ready_date,
	);

	const canSubmit =
		coreFieldsSatisfied &&
		requiredAiFieldsSatisfied &&
		requiredUiFieldsSatisfied;

	const handleSubmit = async () => {
		if (!canSubmit) {
			message.warning(trans("Some required fields are still missing."));
			return;
		}

		// Build the fields array exactly as the existing form does:
		// AI text values first, then UI-handled file/signature values
		const aiFields = draft.fields ?? [];
		const uiHandledFields = uiFields
			.map((schemaField) => {
				if (schemaField.name === "Uploader") {
					const fs = filesByField[schemaField.key] ?? [];
					if (fs.length === 0) return null;
					return {
						key: schemaField.key,
						name: schemaField.name,
						title: schemaField.title,
						value: fs, // File[]
					};
				}
				if (schemaField.name === "Signature") {
					const dataUrl = signaturesByField[schemaField.key];
					if (!dataUrl) return null;
					const f = dataUrlToFile(dataUrl, `signature_${schemaField.key}.png`);
					return {
						key: schemaField.key,
						name: schemaField.name,
						title: schemaField.title,
						value: [f], // also File[] so addIR FormData logic treats it the same
					};
				}
				return null;
			})
			.filter(Boolean);

		const payload = {
			type: draft.type,
			project_id: draft.project_id,
			discipline: draft.discipline,
			zone_id: draft.zone_id ?? null,
			level_id: draft.level_id ?? null,
			room_id: draft.room_id ?? null,
			form_id: draft.form_id,
			ready_date: draft.ready_date,
			fields: [...aiFields, ...uiHandledFields],
		};

		try {
			await addIR(payload).unwrap();
			toast.success(trans("IR Form Submitted Successfully"), {
				autoClose: 1200,
				rtl: localStorage.getItem("lang") === "ar",
			});
			onSubmitted?.();
		} catch (err) {
			console.error("[IRDraftPreview] submit failed", err);
			toast.error(err?.data?.message || trans("Failed to submit IR"), {
				autoClose: 1500,
				rtl: localStorage.getItem("lang") === "ar",
			});
		}
	};

	const renderFieldValue = (field) => {
		if (field.handledBy === "display") return null;
		if (field.handledBy === "ui") return null;
		const entry = (draft?.fields ?? []).find((x) => x.key === field.key);
		const value = entry?.value;
		const filled = value !== null && value !== undefined && value !== "";
		return (
			<div
				key={field.key}
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					gap: S.md,
					padding: "7px 0",
					borderBottom: `1px solid ${C.lineSoft}`,
					fontSize: T.body,
				}}
			>
				<Text
					type="secondary"
					style={{ marginInlineEnd: S.sm, color: C.inkSoft, flexShrink: 0 }}
				>
					{field.title}
					{field.required ? (
						<span style={{ color: C.danger }}> *</span>
					) : null}
				</Text>
				<Text
					strong={filled}
					style={{ textAlign: "end", color: filled ? C.ink : undefined }}
				>
					{filled ? (
						field.name === "DatePicker" ? (
							dayjs(value).format("YYYY-MM-DD")
						) : (
							String(value)
						)
					) : (
						<Text type="secondary" italic style={{ color: C.inkFaint }}>
							{trans("(empty)")}
						</Text>
					)}
				</Text>
			</div>
		);
	};

	const isEmpty = !draft || Object.keys(draft).length === 0;

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<div
				style={{
					flex: 1,
					overflowY: "auto",
					paddingInlineEnd: S.xs,
				}}
			>
				{isEmpty ? (
					<Card {...cardStyles} bodyStyle={{ padding: S.xxl }}>
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description={
								<span style={{ color: C.inkSoft, fontSize: T.small }}>
									{trans(
										"Your IR draft will appear here as you chat with the AI.",
									)}
								</span>
							}
						/>
					</Card>
				) : (
					<>
						{/* Core IR info */}
						<Card
							{...cardStyles}
							title={
								<SectionTitle icon={<InfoCircleOutlined />}>
									{trans("IR Information")}
								</SectionTitle>
							}
						>
							<div>
								<Row label={trans("Type")} value={draft.type} />
								<Row
									label={trans("Project")}
									value={projectName ?? draft.project_id ?? null}
								/>
								<Row
									label={trans("Discipline")}
									value={draft.discipline}
								/>
								<Row
									label={trans("Zone")}
									value={
										zoneName ?? (draft.zone_id ? `#${draft.zone_id}` : null)
									}
								/>
								<Row
									label={trans("Level")}
									value={
										levelName ?? (draft.level_id ? `#${draft.level_id}` : null)
									}
								/>
								<Row
									label={trans("Room")}
									value={
										roomName ?? (draft.room_id ? `#${draft.room_id}` : null)
									}
								/>
								<Row
									label={trans("Ready Date")}
									value={
										draft.ready_date
											? dayjs(draft.ready_date).format("YYYY-MM-DD HH:mm")
											: null
									}
								/>
								<Row
									label={trans("Form")}
									value={
										formSchema?.name ??
										(draft.form_id ? `#${draft.form_id}` : null)
									}
									last
								/>
							</div>
						</Card>

						{/* Form fields (AI-handled) */}
						{formSchema?.fields?.some((f) => f.handledBy === "ai") && (
							<Card
								{...cardStyles}
								title={
									<SectionTitle icon={<CheckCircleOutlined />}>
										{trans("Form Fields")}
									</SectionTitle>
								}
							>
								{formSchema.fields
									.filter((f) => f.handledBy === "ai")
									.map(renderFieldValue)}
							</Card>
						)}

						{/* UI-handled fields: file upload + signature */}
						{uiFields.length > 0 && (
							<Card
								{...cardStyles}
								title={
									<SectionTitle icon={<UploadOutlined />}>
										{trans("Attachments & Signature")}
									</SectionTitle>
								}
							>
								{uiFields.map((f) => {
									if (f.name === "Uploader") {
										return (
											<div key={f.key} style={{ marginBottom: S.md }}>
												<div
													style={{
														marginBottom: S.sm,
														fontSize: T.body,
													}}
												>
													<Text
														type="secondary"
														style={{ color: C.inkSoft }}
													>
														{f.title}
														{f.required ? (
															<span style={{ color: C.danger }}> *</span>
														) : null}
													</Text>
												</div>
												<Upload
													multiple
													beforeUpload={() => false}
													fileList={(filesByField[f.key] ?? []).map(
														(file, i) => ({
															uid: `${f.key}-${i}`,
															name: file.name,
															status: "done",
															originFileObj: file,
														}),
													)}
													onChange={({ fileList }) => {
														const files = fileList.map(
															(item) => item.originFileObj || item,
														);
														setFilesByField((prev) => ({
															...prev,
															[f.key]: files,
														}));
													}}
													onRemove={(file) => {
														setFilesByField((prev) => ({
															...prev,
															[f.key]: (prev[f.key] ?? []).filter(
																(fileObj) => fileObj.name !== file.name,
															),
														}));
													}}
												>
													<Button
														icon={<UploadOutlined />}
														size="small"
														style={{ borderRadius: R.sm }}
													>
														{trans("Add files")}
													</Button>
												</Upload>
											</div>
										);
									}
									if (f.name === "Signature") {
										return (
											<div key={f.key} style={{ marginBottom: S.md }}>
												<div
													style={{
														marginBottom: S.sm,
														fontSize: T.body,
													}}
												>
													<Text
														type="secondary"
														style={{ color: C.inkSoft }}
													>
														{f.title}
														{f.required ? (
															<span style={{ color: C.danger }}> *</span>
														) : null}
													</Text>
												</div>
												<div
													style={{
														border: `1px solid ${C.line}`,
														borderRadius: R.sm,
														background: C.surface,
														overflow: "hidden",
													}}
												>
													<SignatureCanvas
														ref={(el) => {
															sigCanvasRefs.current[f.key] = el;
														}}
														canvasProps={{
															width: 320,
															height: 120,
															style: { width: "100%", height: 120 },
														}}
														onEnd={() => {
															const ref = sigCanvasRefs.current[f.key];
															if (ref && !ref.isEmpty()) {
																setSignaturesByField((prev) => ({
																	...prev,
																	[f.key]: ref
																		.getCanvas()
																		.toDataURL("image/png"),
																}));
															}
														}}
													/>
												</div>
												<Button
													size="small"
													type="link"
													style={{
														paddingInline: 0,
														marginTop: S.xs,
														color: C.accent,
													}}
													onClick={() => {
														sigCanvasRefs.current[f.key]?.clear();
														setSignaturesByField((prev) => {
															const next = { ...prev };
															delete next[f.key];
															return next;
														});
													}}
												>
													{trans("Clear signature")}
												</Button>
											</div>
										);
									}
									return null;
								})}
							</Card>
						)}

						{/* Status hint */}
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: S.sm,
								padding: `${S.md}px ${S.lg}px`,
								background: canSubmit ? C.okSoft : C.warnSoft,
								border: `1px solid ${
									canSubmit ? C.okLine : C.warnLine
								}`,
								borderRadius: R.md,
								fontSize: T.small,
								marginBottom: S.md,
							}}
						>
							{canSubmit ? (
								<Tag
									color="success"
									style={{ margin: 0, borderRadius: R.sm }}
								>
									{trans("Ready to submit")}
								</Tag>
							) : (
								<>
									<Tag
										color="warning"
										style={{ margin: 0, borderRadius: R.sm }}
									>
										{trans("Incomplete")}
									</Tag>
									<span style={{ color: C.inkSoft }}>
										{trans("Keep chatting to fill required fields.")}
									</span>
								</>
							)}
						</div>
					</>
				)}
			</div>

			{/* Footer actions */}
			<div
				style={{
					display: "flex",
					gap: S.sm,
					justifyContent: "flex-end",
					paddingTop: S.md,
					marginTop: S.xs,
					borderTop: `1px solid ${C.line}`,
				}}
			>
				<Button
					icon={<ReloadOutlined />}
					onClick={onReset}
					disabled={submitting}
					style={{ borderRadius: R.sm }}
				>
					{trans("Reset")}
				</Button>
				<Button
					type="primary"
					icon={<SendOutlined />}
					onClick={handleSubmit}
					disabled={!canSubmit || submitting}
					loading={submitting}
					style={{
						borderRadius: R.sm,
						background: !canSubmit || submitting ? undefined : C.accent,
						border: "none",
					}}
				>
					{trans("Submit IR")}
				</Button>
			</div>
		</div>
	);
}

/** Simple two-column row used inside the IR Information card */
function Row({ label, value, last }) {
	const filled = value !== null && value !== undefined && value !== "";
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "baseline",
				gap: S.md,
				padding: "7px 0",
				borderBottom: last ? "none" : `1px solid ${C.lineSoft}`,
			}}
		>
			<Text
				type="secondary"
				style={{ marginInlineEnd: S.sm, color: C.inkSoft, flexShrink: 0 }}
			>
				{label}
			</Text>
			<Text
				strong={filled}
				style={{ textAlign: "end", color: filled ? C.ink : undefined }}
			>
				{filled ? (
					String(value)
				) : (
					<Text type="secondary" italic style={{ color: C.inkFaint }}>
						—
					</Text>
				)}
			</Text>
		</div>
	);
}